import { Navigate } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import type { NavItem, NavLinkItem } from '@/components/AppShell'
import { useAuth } from '@/auth/AuthContext'
import { useCertAccess } from '@/lib/certs'

/**
 * The Certifications module: its own bar, separate from Job Management.
 *
 * The menu is built from the server's capability list (GET /certs/access),
 * never from role names, so widening access in certs_policy.py changes this
 * menu with no front-end edit:
 *
 *   view_matrix          Employees (the matrix)   (Office, Admin today)
 *   manage_requirements  Requirements
 *   view_setup           Setup > Types, Categories & relations
 *   view_notify          Setup > Expiry alerts
 *   view_crew            My crew   (Supervisors; hidden when Employees shows)
 *   view_own             Mine      (technicians; hidden when Employees shows)
 *
 * Office density for office users, field density -- big targets -- for
 * supervisors and technicians, who will be on a phone.
 */
export function CertsShell() {
  const certs = useCertAccess()
  const { hasRole } = useAuth()

  if (!certs.loaded) {
    return <AppShell variant={hasRole('Office') ? 'office' : 'field'} module="certs" nav={[]}>
      <p className="muted">Loading…</p>
    </AppShell>
  }
  if (!certs.enabled) {
    return (
      <AppShell variant={hasRole('Office') ? 'office' : 'field'} module="certs" nav={[]}>
        <div className="alert alert-warn">Certifications aren't switched on for your account.</div>
      </AppShell>
    )
  }

  const nav: NavItem[] = []
  if (certs.can('view_matrix')) nav.push({ to: '/certs', label: 'Employees' })
  if (certs.can('manage_requirements') || certs.can('view_setup')) {
    nav.push({ to: '/certs/requirements', label: 'Requirements' })
  }
  // Setup screens share one dropdown rather than adding buttons to the bar.
  const setup: NavLinkItem[] = []
  if (certs.can('view_setup')) {
    setup.push({ to: '/certs/types', label: 'Types' })
    setup.push({ to: '/certs/setup', label: 'Categories & relations' })
  }
  if (certs.can('view_notify')) setup.push({ to: '/certs/notifications', label: 'Expiry alerts' })
  if (setup.length === 1) nav.push(setup[0])
  else if (setup.length > 1) nav.push({ label: 'Setup', items: setup })
  // My crew and Mine are the supervisor's and technician's screens. Anyone
  // with the full list already reaches every person -- themselves included --
  // from it, so the two would only be clutter on an office bar.
  if (!certs.can('view_matrix')) {
    if (certs.can('view_crew')) nav.push({ to: '/certs/crew', label: 'My crew' })
    if (certs.can('view_own')) nav.push({ to: '/certs/mine', label: 'Mine' })
  }

  return <AppShell variant={hasRole('Office') ? 'office' : 'field'} module="certs" nav={nav} />
}

/** /certs itself: the most useful screen this person can open. */
export function CertsHome({ matrix }: { matrix: JSX.Element }) {
  const certs = useCertAccess()
  // Wait for the capability list. Deciding before it arrives reads "not
  // loaded" as "not allowed" and sent Admins to their own page (13 Oct 2026).
  if (!certs.loaded) return <p className="muted">Loading…</p>
  if (certs.can('view_matrix')) return matrix
  if (certs.can('view_crew')) return <Navigate to="/certs/crew" replace />
  return <Navigate to="/certs/mine" replace />
}
